import { useState } from 'react';
import { createProject } from '../api/client.js';
import { useDictionary } from '../i18n/context.js';

// ---------------------------------------------------------------------------
// Whole-branch review finding I3: Home's "Create project" button had no
// `onClick` and `api/client.ts` had no create function, so nothing could be
// created through a supported surface on a deployed instance. The plan
// defers the five-step project wizard (UI structure spec section 6) but
// says "a plain create form replaces it" -- this is that form: the fields
// `POST /api/projects` (apps/worker/src/index.ts) already requires, and
// nothing else.
// ---------------------------------------------------------------------------

export type CreateProjectFormProps = {
  onCreated: (projectId: string) => void;
  onCancel: () => void;
};

export const CreateProjectForm = ({ onCreated, onCancel }: CreateProjectFormProps): React.JSX.Element => {
  const dict = useDictionary();
  const [name, setName] = useState('');
  const [website, setWebsite] = useState('');
  const [dailyCap, setDailyCap] = useState('5');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const parsedDailyCap = Number(dailyCap);
  const dailyCapValid = dailyCap.trim() !== '' && Number.isInteger(parsedDailyCap) && parsedDailyCap >= 0;

  const submit = async (): Promise<void> => {
    if (name.trim() === '') {
      setError(dict.ui.createProject.nameRequired);
      return;
    }
    if (!dailyCapValid) {
      setError(dict.ui.createProject.dailyCapInvalid);
      return;
    }
    setError(null);
    setBusy(true);
    try {
      const { project } = await createProject({
        name: name.trim(),
        website: website.trim() === '' ? null : website.trim(),
        dailyCap: parsedDailyCap,
      });
      onCreated(project.id);
    } catch (err) {
      setError(err instanceof Error ? err.message : dict.ui.createProject.createFailed);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="panel">
      <div className="panel__head"><h2 className="t-section">{dict.ui.createProject.title}</h2></div>
      <div className="panel__body stack">
        {error && (
          <div className="alert alert--danger">
            <div className="alert__body">{error}</div>
          </div>
        )}

        <div className="field" style={{ maxWidth: 420 }}>
          <label className="field__label" htmlFor="create-project-name">{dict.ui.createProject.nameLabel}</label>
          <input
            id="create-project-name"
            className="input"
            type="text"
            value={name}
            disabled={busy}
            onChange={(e) => { setName(e.target.value); setError(null); }}
          />
        </div>

        <div className="field" style={{ maxWidth: 420 }}>
          <label className="field__label" htmlFor="create-project-website">{dict.ui.createProject.websiteLabel}</label>
          <input
            id="create-project-website"
            className="input"
            type="text"
            value={website}
            disabled={busy}
            onChange={(e) => { setWebsite(e.target.value); setError(null); }}
          />
        </div>

        <div className="field" style={{ maxWidth: 220 }}>
          <label className="field__label" htmlFor="create-project-daily-cap">{dict.ui.createProject.dailyCapLabel}</label>
          <input
            id="create-project-daily-cap"
            className={dailyCap.trim() !== '' && !dailyCapValid ? 'input input--error' : 'input'}
            type="number"
            min={0}
            step={1}
            value={dailyCap}
            disabled={busy}
            onChange={(e) => { setDailyCap(e.target.value); setError(null); }}
          />
          <div className="field__foot">
            <span className="field__hint">{dict.ui.createProject.dailyCapHint}</span>
          </div>
        </div>

        <div className="row" style={{ gap: 'var(--sp-3)' }}>
          <button type="button" className="btn btn--primary" disabled={busy} onClick={() => { void submit(); }}>
            {busy ? dict.ui.createProject.creating : dict.ui.createProject.submit}
          </button>
          <button type="button" className="btn btn--ghost" disabled={busy} onClick={onCancel}>
            {dict.ui.createProject.cancel}
          </button>
        </div>
      </div>
    </div>
  );
};
